
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Output, signal, computed, inject, input } from "@angular/core";
import { Router } from "@angular/router";
import { Permissions } from "@main/permissions/permissions";
import { AppOnlineStateService } from "@main/pwa/app-online-state.service";
import { CoreRole } from "@main/roles";
import { PatientLogin } from "@main/users";
import { UserHelperService } from "@main/users/user-helper.service";
import { ConsultationMergeMoveService } from "@project/consultation-merge-move/consultation-merge-move.service";
import { IEpisode } from "@project/models/episodes/episode";
import { EpisodeStatus } from "@project/models/episodes/episode-status";
import { PlayerOfflineStorage } from "@project/player/player-offline.storage";
import { WalkInService } from "@project/walk-ins/classes/walk-in.service";
import { ConfirmationService, MenuItem } from "primeng/api";
import { OverlayPanel } from "primeng/overlaypanel";
import { toSignal } from "@angular/core/rxjs-interop";
import { EpisodeService } from "./episode.service";
import { PatientService } from "@main/app-layout/components/patient-switch/patient.service";
import { isNoteDraftEpisode } from "./episode-select/episode-defaults";
import { FormEventType } from "@main/formio/components/form-events/event-type";
import { FormEventPayload } from "@main/formio/dto/formEventsDTO";
import { FormEventsService } from "@main/formio/services/formEvents.service";

@Component({
    selector: "episode-header-actions",
    templateUrl: "./episode-header-actions.component.html",
    styleUrls: ["./episode-header-actions.component.scss"]
})
export class EpisodeHeaderActionsComponent implements AfterViewInit {
    private readonly appOnlineStateService = inject(AppOnlineStateService);
    private readonly episodeService = inject(EpisodeService);
    private readonly patientService = inject(PatientService);
    private readonly router = inject(Router);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly userHelperService = inject(UserHelperService);
    private readonly walkInService = inject(WalkInService);
    private readonly mergeMoveService = inject(ConsultationMergeMoveService);
    private readonly formEventsService = inject(FormEventsService);
    private readonly cd = inject(ChangeDetectorRef);

    showPatientDialog: boolean = false;

    readonly offlinePatient = signal<PatientLogin | null>(null);
    readonly timesOfflinePatientAlert = signal<number>(0);
    readonly mergedEpisodes = signal<IEpisode[]>([]);
    readonly loadingMergedEpisodes = signal<boolean | null>(null);

    readonly episode = input<IEpisode | null>(null);

    readonly showBanButton = input<boolean>(true);
    readonly episodeOptionsButtonBanItems = input<MenuItem[]>([]);

    readonly showPlusButton = input<boolean>(true);
    readonly episodeOptionsButtonPlusItems = input<MenuItem[]>([]);

    readonly showEllipsisButton = input<boolean>(true);
    readonly episodeOptionsButtonEllipsisItems = input<MenuItem[]>([]);

    readonly showViewButton = input<boolean>(false);

    readonly isMergeMoveModeOn = toSignal<boolean>(this.mergeMoveService.isMergeMoveModeOn$);

    readonly showBanButtonComputed = computed<boolean>(() => {
        return this.showBanButton() &&
               this.episodeOptionsButtonBanItems()?.some(o => o.visible) &&
               !this.isWalkInDraft();
        });

    readonly showPlusButtonComputed = computed<boolean>(() => {
        return this.showPlusButton() &&
               this.episodeOptionsButtonPlusItems()?.some(o => o.visible) &&
               !this.isNoteDraftEpisode()
    });

    readonly showEllipsisButtonComputed = computed<boolean>(() => {
        return this.showEllipsisButton() &&
               this.episodeOptionsButtonEllipsisItems()?.some(o => o.visible ?? true) &&
               !this.isNoteDraftEpisode();
    });

    readonly isLocal = computed<boolean>(() => this.episode()?.state === EpisodeStatus.StoredLocal);
    readonly isDraft = computed<boolean>(() => {
        const state = this.episode()?.state;
        return state === EpisodeStatus.Draft || state === EpisodeStatus.DraftPrescription;
    });
    readonly isWalkInDraft = computed<boolean>(() => this.episode()?.state === EpisodeStatus.WalkInDraft);
    readonly isNoteDraftEpisode = computed<boolean>(() => this.episode() ? isNoteDraftEpisode(this.episode()!) : false);

    readonly isLimitedAccess = computed<boolean>(() => this.episode()?.isGenericEpisodeWithLimitedAccess ?? false);

    readonly createdDateInfo = computed<string>(() => {
        const ep = this.episode();
        if (!ep || ep.isGenericEpisodeWithLimitedAccess) return null;
        return `Created: ${this.episodeService.formatDate(ep.createDateTime)} by:`;
    });

    readonly updatedDateInfo = computed<string>(() => {
        const ep = this.episode();
        if (!ep || ep.isGenericEpisodeWithLimitedAccess) return null;
        return `Last updated: ${this.episodeService.formatDate(ep.updateDateTime)} by:`;
    });

    readonly ravenIssueStatus = computed<string>(() => {
        const ep = this.episode();
        return ep?.ravenIssueId && ep.ravenIssue
            ? `Status: ${ep.ravenIssue.ravenStatus.name}`
            : null;
    });

    readonly ravenIssueRN = computed<string>(() => {
        const ep = this.episode();
        return ep?.ravenIssueId && ep.ravenIssue
            ? `Reference Number: ${ep.ravenIssue.referenceNumber}`
            : null;
    });

    readonly organizationName = computed<string>(() => {
        const ep = this.episode();
        if (!ep || ep.isGenericEpisodeWithLimitedAccess) return null;

        return ep.state === EpisodeStatus.PatientUploadedDocuments
            ? "Patient"
            : ep.organizationName ?? "Unknown";
    });

    get isOnline(): boolean {
        return this.appOnlineStateService.isAppOnline;
    }

    @Output() readonly refreshEpisodes = new EventEmitter<void>();
    @Output() readonly viewEpisode = new EventEmitter<string>();

    ngAfterViewInit() {
        this.offlinePatient.set(PlayerOfflineStorage.get(this.patientService.currentPatientId));
        if (this.isOnline && !!this.offlinePatient()) {
            this.showCreatePatientOnlineMsg();
        }
    }

    async sendEpisodeFromCacheToServer() {
        this.offlinePatient.set(PlayerOfflineStorage.get(this.episode().patientUserId));

        if (!!this.offlinePatient()) {
            this.showCreatePatientOnlineMsg();
        } else {
            await this.router.navigate(["/app/consultation-history/episode", this.episode().id], {
                queryParams: { isSaveOnline: true }
            });
        }
    }

    showCreatePatientOnlineMsg() {
        this.confirmationService.confirm({
            message: "The DRAFT OFFLINE Patient needs to be Saved ONLINE first.  Do you want to save it now?",
            accept: () => {
                this.showPatientDialog = true;
                this.cd.detectChanges();
            }
        });
    }
    closedPatientOnlineMsg() {
        if (this.timesOfflinePatientAlert() == 2) {
            this.router.navigate(["/app/consultation-history"]);
        } else {
            this.timesOfflinePatientAlert.set(this.timesOfflinePatientAlert() + 1);
        }
    }

    onPatientSaved(event: any) {
        this.patientService.currentPatientId = event.masterProfileId;
    }
    navigateToEditEpisodeView() {
        this.router.navigate(["/app/consultation-history/episode", this.episode().id]);
    }

    get isApproveDraftButtonVisible(): boolean {
        const hasConsultationHistoryWrite = this.userHelperService.hasPermission(Permissions.ConsultationHistoryWrite);
        const isDraft = this.episode().state == EpisodeStatus.Draft;
        const isClinicalAdmin = this.userHelperService.hasRole(CoreRole.ClinicalAdmin);
        const isEpisodeOwner = this.userHelperService.userId == this.episode().assignedClinicianUserId;

        return hasConsultationHistoryWrite && isDraft && (isClinicalAdmin || isEpisodeOwner);
    }

    get isSaveWalkInDraftButtonVisible(): boolean {
        const hasWriteDiary = this.userHelperService.hasPermission(Permissions.WriteDiary);
        const isClinicalOrDoctor = this.userHelperService.hasRole(CoreRole.ClinicalAdmin) ||
                                   this.userHelperService.hasRole(CoreRole.Doctor);
        const isWalkInClinician = this.userHelperService.userId == this.episode().walkInClinicianId;

        return hasWriteDiary && this.isWalkInDraft() && (isClinicalOrDoctor || isWalkInClinician);
    }

    async approveWalkInDraftEpisode() {
        if (!this.isWalkInDraft()) {
            return;
        }
        const walkIn = await this.walkInService.approveByEpisode(this.episode().id);
        if(!walkIn) return;

        this.formEventsService.triggerFormEvent(<FormEventPayload>{
            formEventType: FormEventType.ApproveWalkIn,
            episodeId: walkIn.episodeId,
            patientId: walkIn.patientId,
            walkInId: walkIn.id
        });
        this.refreshEpisodes.emit();
    }

    async getMergedInfo(mergedInfoOverlay: OverlayPanel, event: Event) {
        if (this.loadingMergedEpisodes()) {
            mergedInfoOverlay?.toggle(event);
            return;
        }

        try {
            this.loadingMergedEpisodes.set(true);
            this.mergedEpisodes.set(await this.episodeService.getMergedEpisodes(this.episode().id));
        } finally {
            this.loadingMergedEpisodes.set(false);
            mergedInfoOverlay?.toggle(event);
        }
    }

    emitViewEpisode() {
        this.viewEpisode.emit(this.episode().id);
    }
}
